import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import {
  boardFilterFacetSchema,
  boardFilterKeywordSchema,
  boardFilterQuerySchema,
  dueBucketSchema,
} from './board-filter';
import { CHECKLIST_ITEM_TEXT_MAX_LENGTH, CHECKLIST_TITLE_MAX_LENGTH } from './checklist';
import { ulidSchema } from './id';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { RANK_MAX_LENGTH } from './rank';
import { WORKSPACE_NAME_MAX_LENGTH, WORKSPACE_SHORT_NAME_MAX_LENGTH } from './workspace';

/**
 * Calendar view contracts — block B51.
 *
 * These are route-specific read models for board and Workspace Calendar reads.
 * Calendar is due-date keyed, so entry dates are modelled on the entry rather
 * than by widening card/table/board-face contracts.
 */

export const WORKSPACE_CALENDAR_BOARD_LIMIT = 20;

const calendarInstantQuerySchema = z.iso.datetime({ offset: true });

const calendarRangeQueryShape = {
  from: calendarInstantQuerySchema,
  to: calendarInstantQuerySchema,
} as const;

function repeatedQueryValue(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return Array.from(value as unknown[]);
  return [value];
}

const ulidArrayQuerySchema = z.preprocess(repeatedQueryValue, z.array(ulidSchema));
const dueBucketArrayQuerySchema = z.preprocess(
  repeatedQueryValue,
  z.array(dueBucketSchema).min(1),
);

function isIncreasingRange(value: { from: string; to: string }): boolean {
  return Date.parse(value.from) < Date.parse(value.to);
}

const increasingRangeIssue = {
  message: 'from must be before to',
  path: ['to'],
};

export const calendarRangeQuerySchema = z
  .strictObject(calendarRangeQueryShape)
  .refine(isIncreasingRange, increasingRangeIssue)
  .describe('Calendar range query using half-open [from, to) semantics.');

export type CalendarRangeQuery = z.infer<typeof calendarRangeQuerySchema>;

export const boardCalendarQuerySchema = boardFilterQuerySchema
  .extend({
    ...calendarRangeQueryShape,
    lists: ulidArrayQuerySchema.pipe(z.array(ulidSchema).min(1)).optional(),
  })
  .refine(isIncreasingRange, increasingRangeIssue);

export type BoardCalendarQuery = z.infer<typeof boardCalendarQuerySchema>;

export const workspaceCalendarQuerySchema = z
  .strictObject({
    ...calendarRangeQueryShape,
    boardIds: ulidArrayQuerySchema
      .pipe(z.array(ulidSchema).min(1).max(WORKSPACE_CALENDAR_BOARD_LIMIT))
      .describe(
        'Selected board ids for this Workspace Calendar request. Repeat the ' +
          'key for several boards; 1 to 20 are accepted.',
      ),
    members: boardFilterFacetSchema.optional(),
    labels: boardFilterFacetSchema.optional(),
    lists: ulidArrayQuerySchema.pipe(z.array(ulidSchema).min(1)).optional(),
    due: dueBucketArrayQuerySchema.optional(),
    keyword: boardFilterKeywordSchema.optional(),
  })
  .refine(isIncreasingRange, increasingRangeIssue);

export type WorkspaceCalendarQuery = z.infer<typeof workspaceCalendarQuerySchema>;

export const calendarRangeSchema = z
  .object(calendarRangeQueryShape)
  .refine(isIncreasingRange, increasingRangeIssue);

export type CalendarRange = z.infer<typeof calendarRangeSchema>;

export const calendarBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  closed: z.boolean(),
});

export type CalendarBoardRef = z.infer<typeof calendarBoardRefSchema>;

export const calendarWorkspaceRefSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  shortName: z.string().max(WORKSPACE_SHORT_NAME_MAX_LENGTH),
});

export type CalendarWorkspaceRef = z.infer<typeof calendarWorkspaceRefSchema>;

export const calendarListRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  position: z.string().max(RANK_MAX_LENGTH),
});

export type CalendarListRef = z.infer<typeof calendarListRefSchema>;

export const calendarCardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN` for a viewer who may use the board's references, else `null` — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
});

export type CalendarCardRef = z.infer<typeof calendarCardRefSchema>;

export const calendarChecklistRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CHECKLIST_TITLE_MAX_LENGTH),
  position: z.string().max(RANK_MAX_LENGTH),
});

export type CalendarChecklistRef = z.infer<typeof calendarChecklistRefSchema>;

export const calendarChecklistItemRefSchema = z.object({
  id: ulidSchema,
  text: z.string().max(CHECKLIST_ITEM_TEXT_MAX_LENGTH),
  position: z.string().max(RANK_MAX_LENGTH),
  checked: z.boolean(),
  dueAt: z.string().min(1),
});

export type CalendarChecklistItemRef = z.infer<typeof calendarChecklistItemRefSchema>;

export const boardCalendarCardEntrySchema = z.object({
  type: z.literal('card'),
  card: calendarCardRefSchema,
  list: calendarListRefSchema,
  startAt: z.string().min(1).nullable(),
  dueAt: z.string().min(1),
  /** `cards.due_all_day` — FR-EXP-029 edge case (BE-44.7): date-only dues render as the same calendar date. */
  dueAllDay: z.boolean(),
  completed: z.boolean(),
});

export type BoardCalendarCardEntry = z.infer<typeof boardCalendarCardEntrySchema>;

export const boardCalendarChecklistItemEntrySchema = z.object({
  type: z.literal('checklist_item'),
  card: calendarCardRefSchema,
  list: calendarListRefSchema,
  checklist: calendarChecklistRefSchema,
  checklistItem: calendarChecklistItemRefSchema,
  dueAt: z.string().min(1),
  completed: z.boolean(),
});

export type BoardCalendarChecklistItemEntry = z.infer<
  typeof boardCalendarChecklistItemEntrySchema
>;

export const boardCalendarEntrySchema = z.discriminatedUnion('type', [
  boardCalendarCardEntrySchema,
  boardCalendarChecklistItemEntrySchema,
]);

export type BoardCalendarEntry = z.infer<typeof boardCalendarEntrySchema>;

export const workspaceCalendarCardEntrySchema = boardCalendarCardEntrySchema.extend({
  board: calendarBoardRefSchema,
});

export type WorkspaceCalendarCardEntry = z.infer<typeof workspaceCalendarCardEntrySchema>;

export const workspaceCalendarChecklistItemEntrySchema =
  boardCalendarChecklistItemEntrySchema.extend({
    board: calendarBoardRefSchema,
  });

export type WorkspaceCalendarChecklistItemEntry = z.infer<
  typeof workspaceCalendarChecklistItemEntrySchema
>;

export const workspaceCalendarEntrySchema = z.discriminatedUnion('type', [
  workspaceCalendarCardEntrySchema,
  workspaceCalendarChecklistItemEntrySchema,
]);

export type WorkspaceCalendarEntry = z.infer<typeof workspaceCalendarEntrySchema>;

export const boardCalendarSchema = z.object({
  board: calendarBoardRefSchema,
  range: calendarRangeSchema,
  entries: z.array(boardCalendarEntrySchema),
  excludedCardCount: z.number().int().nonnegative(),
});

export type BoardCalendar = z.infer<typeof boardCalendarSchema>;

export const workspaceCalendarSchema = z.object({
  workspace: calendarWorkspaceRefSchema,
  selectedBoardIds: z.array(ulidSchema).max(WORKSPACE_CALENDAR_BOARD_LIMIT),
  range: calendarRangeSchema,
  entries: z.array(workspaceCalendarEntrySchema),
  excludedCardCount: z.number().int().nonnegative(),
});

export type WorkspaceCalendar = z.infer<typeof workspaceCalendarSchema>;
